// src/utils/testNavigation.js

import { useNavigate } from "react-router-dom";

// Test type to base route mapping
const TEST_TYPE_ROUTES = {
  full_mock: "mock-tests",
  mini_mock: "mini-mock-tests",
  class_test: "class-tests",
};

// Test type to back route mapping
const TEST_TYPE_BACK_ROUTES = {
  full_mock: "/app/mocktests",
  mini_mock: "/app/minimocktests",
  class_test: "/app/classtests",
};

/**
 * Custom hook for test-related navigation utilities
 * Provides URL generation for test routes
 */
export const useTestNavigation = () => {
  const navigate = useNavigate();

  const getAttemptsUrl = (testId, testType = "full_mock", options = {}) => {
    // If it's a bundle test, use bundle-specific URL
    if (options.isBundle && options.bundleId && options.module) {
      return `/app/bundles/${options.bundleId}/test/${testId}/${options.module}/attempts`;
    }
    
    // Otherwise, use regular mock test URL
    const route = TEST_TYPE_ROUTES[testType] || "mock-tests";
    return `/app/${route}/${testId}/attempts`;
  };

  const getResultUrl = (testId, attemptId, testType = "full_mock", options = {}) => {
    // If it's a bundle test, use bundle-specific URL
    if (options.isBundle && options.bundleId && options.module) {
      return `/app/bundles/${options.bundleId}/test/${testId}/${options.module}/attempt/${attemptId}/result`;
    }
    
    // Otherwise, use regular mock test URL
    const route = TEST_TYPE_ROUTES[testType] || "mock-tests";
    return `/app/${route}/${testId}/attempt/${attemptId}/result`;
  };

  const getDetailsUrl = (testId, attemptId, testType = "full_mock", options = {}) => {
    // If it's a bundle test, use bundle-specific URL
    if (options.isBundle && options.bundleId && options.module) {
      return `/app/bundles/${options.bundleId}/test/${testId}/${options.module}/attempt/${attemptId}/details`;
    }
    
    // Otherwise, use regular mock test URL
    const route = TEST_TYPE_ROUTES[testType] || "mock-tests";
    return `/app/${route}/${testId}/attempt/${attemptId}/details`;
  };

  const getLogUrl = (testId, testType = "full_mock") => {
    const route = TEST_TYPE_ROUTES[testType] || "mock-tests";
    return `/app/${route}/${testId}/log`;
  };

  const getBackRoute = (testType = "full_mock") => {
    return TEST_TYPE_BACK_ROUTES[testType] || "/app/mocktests";
  };

  const navigateToAttempts = (testId) => {
    navigate(getAttemptsUrl(testId));
  };

  const navigateToResult = (testId, attemptId) => {
    navigate(getResultUrl(testId, attemptId));
  };

  const navigateToDetails = (testId, attemptId) => {
    navigate(getDetailsUrl(testId, attemptId));
  };

  const navigateToLog = (testId) => {
    navigate(getLogUrl(testId));
  };

  return {
    getAttemptsUrl,
    getResultUrl,
    getAttemptResultUrl: getResultUrl, // Alias for compatibility
    getDetailsUrl,
    getLogUrl,
    getBackRoute,
    navigateToAttempts,
    navigateToResult,
    navigateToDetails,
    navigateToLog,
    navigate,
  };
};

export default useTestNavigation;
